import React, { useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import GroupsIcon from "@mui/icons-material/Groups";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import ChatIcon from "@mui/icons-material/Chat";
import { useAuth } from "../../firebase/authContext";
import { useSocial } from "../../context/SocialContext";
import AuthModal from "./AuthModal";

const UserMenu = () => {
  const { currentUser, userProfile, isAdmin, logout, loading } = useAuth();
  const { openSocialModal } = useSocial();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path) => {
    setAnchorEl(null);
    navigate(path);
  };

  const handleLogout = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setAuthModalOpen(false);
    handleCloseMenu();
    await logout();
  };

  if (loading) {
    return (
      <Skeleton
        variant="circular"
        width={32}
        height={32}
        animation="wave"
        aria-label="账号加载中"
        aria-busy="true"
        data-testid="header-auth-skeleton"
      />
    );
  }

  if (!currentUser) {
    return (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AccountCircleIcon fontSize="small" />}
          onClick={() => setAuthModalOpen(true)}
          aria-label="登录 / 注册"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            fontSize: { xs: "0.75rem", sm: "0.85rem" },
            px: { xs: 0.75, sm: 1.5 },
            py: 0.5,
            minWidth: { xs: 0, sm: "auto" },
            "& .MuiButton-startIcon": {
              mr: { xs: 0, sm: 0.75 },
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            登录 / 注册
          </Box>
        </Button>
        {authModalOpen ? (
          <AuthModal
            open
            onClose={() => setAuthModalOpen(false)}
          />
        ) : null}
      </>
    );
  }

  const displayName =
    userProfile?.displayName ||
    currentUser.displayName ||
    currentUser.email?.split("@")[0] ||
    "读者";

  const initial = (displayName[0] || "读").toUpperCase();

  return (
    <>
      <Tooltip title={`当前账号：${displayName}`}>
        <IconButton
          onClick={handleOpenMenu}
          size="small"
          aria-label={`当前账号：${displayName}`}
          aria-haspopup="menu"
          sx={{
            p: 0.25,
            border: "2px solid",
            borderColor: isAdmin ? "secondary.main" : "primary.main",
          }}
        >
          {currentUser.photoURL ? (
            <Avatar
              src={currentUser.photoURL}
              alt={displayName}
              sx={{ width: 28, height: 28 }}
            />
          ) : (
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: "0.85rem",
                bgcolor: isAdmin ? "secondary.main" : "primary.main",
                fontWeight: 700,
              }}
            >
              {initial}
            </Avatar>
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 230,
            borderRadius: 2.5,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            overflow: "visible",
          },
        }}
      >
        <Box
          onClick={() => handleNavigate("/profile")}
          sx={{
            px: 2,
            py: 1.5,
            cursor: "pointer",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {displayName}
            </Typography>
            {isAdmin && (
              <Chip
                label="管理员"
                size="small"
                color="secondary"
                sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
              />
            )}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {currentUser.email}
          </Typography>
        </Box>

        <Divider />

        <MenuItem onClick={() => handleNavigate("/profile")}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="个人资料与设置" />
        </MenuItem>

        <MenuItem onClick={() => handleNavigate("/notes")}>
          <ListItemIcon>
            <NoteAltIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="我的灵修笔记" />
        </MenuItem>

        <MenuItem onClick={() => handleNavigate("/history")}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="阅读记录与进度" />
        </MenuItem>

        <MenuItem onClick={() => handleNavigate("/community")}>
          <ListItemIcon>
            <GroupsIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="社区圣经分享" />
        </MenuItem>

        {!currentUser.isGuest && currentUser.uid !== "local_guest_user" && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              openSocialModal(0);
            }}
          >
            <ListItemIcon>
              <ChatIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="主内好友与私信" />
          </MenuItem>
        )}

        {isAdmin && (
          <MenuItem onClick={() => handleNavigate("/admin")}>
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText
              primary="后台管理中心"
              primaryTypographyProps={{ fontWeight: 700, color: "secondary.main" }}
            />
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="退出登录" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
